import React, { useState, useEffect, useMemo } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  InlineLoading,
  InlineNotification,
  Tag,
  Button,
  Dropdown,
  MultiSelect,
  DatePicker,
  DatePickerInput,
} from "@carbon/react";
import { Filter, } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";

// Helper function to get tag color based on name
const getTagColor = (tagName) => {
  const colors = {
    'best_value': 'green',
    'luxury': 'purple',
    'urgent_sale': 'red',
    'new_listing': 'blue',
    'featured': 'cyan',
    'price_reduced': 'orange'
  };
  return colors[tagName?.toLowerCase()] || 'gray';
};

export default function TagsReport() {
  // Tag filter states
  const [selectedTag, setSelectedTag] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const [startDate, setStartDate] = useState("");
const [endDate, setEndDate] = useState("");
const [verificationStatus, setVerificationStatus] = useState(null);
const [status, setStatus] = useState(null);
const [propertyType, setPropertyType] = useState(null);

  // Table data states
  const [tableData, setTableData] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [summary, setSummary] = useState({
    totalUniqueTags: 0,
    totalPropertiesWithTags: 0,
    totalPropertiesWithoutTags: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Search logic
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch tags list on component mount
  useEffect(() => {
    fetchTagsList();
  }, []);
  useEffect(() => {
  
}, [tagsList]);

  // Fetch Tags List for dropdown
  const fetchTagsList = async () => {
    setLoadingTags(true);
    try {
      const response = await callApi("GET", "/properties/tags-report");
 
      if (response?.status_code === 200 && response?.data) {
        const tags = response.data.tags || [];
        const mappedTags = tags.map((tag) => ({
          id: tag.tag_name,
          text: tag.tag_name.replace(/_/g, " ").toUpperCase(),
          originalName: tag.tag_name,
          count: tag.count,
}));



setTagsList(mappedTags);
      }
    } catch (error) {
      
      setError("Failed to load tags list. Please try again.");
    } finally {
      setLoadingTags(false);
    }
  };

const applyFilters = async () => {
  if (!selectedTag || selectedTag.length === 0) {
    setError("Please select a tag to view properties");
    return;
  }

  setLoading(true);
  setError("");

  try {
const params = {tag_names: selectedTag.map(tag => tag.originalName).join(","),};

if (startDate) {
  params.start_date = startDate;
}

if (endDate) {
  params.end_date = endDate;
}

if (verificationStatus) {
  params.verification_status = verificationStatus;
}

if (status) {
  params.status = status;
}

if (propertyType) {
  params.property_type = propertyType;
}

const queryParams = new URLSearchParams(params);
    const response = await callApi(
      "GET",
      `/properties/tags-report?${queryParams.toString()}`
    );

    if (response?.status_code === 200 && response?.data) {
      const data = response.data;

      setSummary({
        totalUniqueTags: data.total_unique_tags || 0,
        totalPropertiesWithTags: data.total_properties_with_tags || 0,
        totalPropertiesWithoutTags: data.total_properties_without_tags || 0
      });

     const properties = data.tags?.[0]?.properties || [];

      const formattedData = properties.map((property, index) => ({
        id: String(property.property_id || index + 1),
        propertySeqId: property.property_seq_id || "-",
        propertyTitle: property.property_title || "-",
        propertyType: property.property_type || "-",
        verificationStatus: property.verification_status || "-",
        status: property.status || "-",
        createdTime: property.created_timestamp
          ? new Date(property.created_timestamp).toLocaleString()
          : "-",
        postedFor: property.property_posted_for || "-",
        priceRange: property.starting_price
  ? `₹${property.starting_price.toLocaleString()}`
  : "-",
      }));

      setTableData(formattedData);

      setTableHeaders([
        { key: "propertySeqId", header: "Property ID" },
        { key: "propertyTitle", header: "Property Title" },
        { key: "propertyType", header: "Property Type" },
        { key: "verificationStatus", header: "Verification Status" },
        { key: "status", header: "Status" },
        { key: "createdTime", header: "Created At" },
        { key: "postedFor", header: "Posted For" },
        { key: "priceRange", header: "Price Range" },
      ]);

      setPage(1);
    }
  } catch (error) {
    
    setError("Failed to fetch tag report. Please try again.");
    setTableData([]);
  } finally {
    setLoading(false);
  }
};

  // Apply search filter
  const filteredData = useMemo(() => {
    let filtered = [...tableData];
    
    if (debouncedSearch) {
      filtered = filtered.filter(row =>
        JSON.stringify(Object.values(row))
          .toLowerCase()
          .includes(debouncedSearch)
      );
    }
    
    return filtered;
  }, [tableData, debouncedSearch]);

  const paginatedData = useMemo(() =>
    filteredData.slice((page - 1) * pageSize, page * pageSize),
    [filteredData, page, pageSize]
  );

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedTag([]);
    setTableData([]);
    setSearchTerm("");
    setVerificationStatus(null);
    setStatus(null);
    setPropertyType(null);
      setStartDate("");
  setEndDate("");
    setPage(1);
    setSummary({
      totalUniqueTags: 0,
      totalPropertiesWithTags: 0,
      totalPropertiesWithoutTags: 0
    });
  };

  // Check if any filter is active
  const hasActiveFilters = selectedTag.length > 0;


  return (
    <>
      {/* Summary Cards */}
      {summary.totalUniqueTags > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "1rem",
          marginBottom: "1rem"
        }}>
          <div style={{
            background: "#f4f4f4",
            padding: "1rem",
            borderRadius: "8px",
            borderLeft: "4px solid #0f62ac"
          }}>
            <div style={{ fontSize: "14px", color: "#525252" }}>Total Unique Tags</div>
            <div style={{ fontSize: "32px", fontWeight: "bold", color: "#161616" }}>
              {summary.totalUniqueTags}
            </div>
          </div>
          
          <div style={{
            background: "#f4f4f4",
            padding: "1rem",
            borderRadius: "8px",
            borderLeft: "4px solid #24a148"
          }}>
            <div style={{ fontSize: "14px", color: "#525252" }}>Properties With Tags</div>
            <div style={{ fontSize: "32px", fontWeight: "bold", color: "#161616" }}>
              {summary.totalPropertiesWithTags}
            </div>
          </div>
          
          <div style={{
            background: "#f4f4f4",
            padding: "1rem",
            borderRadius: "8px",
            borderLeft: "4px solid #da1e28"
          }}>
            <div style={{ fontSize: "14px", color: "#525252" }}>Properties Without Tags</div>
            <div style={{ fontSize: "32px", fontWeight: "bold", color: "#161616" }}>
              {summary.totalPropertiesWithoutTags}
            </div>
          </div>
        </div>
      )}

      {/* Tags Filter Bar */}
      <div
        style={{
          padding: "1rem",
          backgroundColor: "#f4f4f4",
          borderRadius: "8px",
          marginBottom: "1rem",
          border: "1px solid #e0e0e0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "1rem",
            gap: "0.5rem",
          }}
        >
          <Filter />
          <h4 style={{ margin: 0 }}>
            Filter Tag
          </h4>
        </div>

        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: "250px" }}>
           <MultiSelect
            id="tag-filter"
            titleText="Select Tags"
            label={loadingTags ? "Loading..." : "Choose tags"}
            items={tagsList}
            itemToString={(item) => item?.text || ""}
            selectedItems={selectedTag}
            onChange={({ selectedItems }) => {
                setSelectedTag(selectedItems);
                setTableData([]);
                setSearchTerm("");
                setPage(1);
            }}
            disabled={loadingTags}
            />
          </div>

          <Dropdown
            id="verification-status"
            titleText="Verification Status"
            label="Choose"
            items={["Verified", "Pending"]}
            selectedItem={verificationStatus}
            onChange={({ selectedItem }) =>
                setVerificationStatus(selectedItem)
            }
            />
      <Dropdown
        id="property-type"
        titleText="Property Type"
        label="Choose"
        items={["Residential", "Commercial", "Land/Plot"]}
        selectedItem={propertyType}
        onChange={({ selectedItem }) =>
            setPropertyType(selectedItem)
        }
        />


<div style={{ minWidth: "180px" }}>
  <DatePicker
    datePickerType="single"
    dateFormat="Y-m-d"
    value={startDate ? [startDate] : []}
    onChange={(selectedDates) => {
      if (selectedDates?.length > 0) {
        const date = selectedDates[0];

        const formattedDate = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        setStartDate(formattedDate);
      } else {
        setStartDate("");
      }
    }}
  >
    <DatePickerInput
      id="start-date"
      labelText="Start Date"
      placeholder="yyyy-mm-dd"
    />
  </DatePicker>
</div>

<div style={{ minWidth: "180px" }}>
  <DatePicker
    datePickerType="single"
    dateFormat="Y-m-d"
    value={endDate ? [endDate] : []}
    onChange={(selectedDates) => {
      if (selectedDates?.length > 0) {
        const date = selectedDates[0];

        const formattedDate = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        setEndDate(formattedDate);
      } else {
        setEndDate("");
      }
    }}
  >
    <DatePickerInput
      id="end-date"
      labelText="End Date"
      placeholder="yyyy-mm-dd"
    />
  </DatePicker>
</div>


          
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.75rem",
            }}
          >
            <Button kind="secondary" onClick={clearAllFilters}>
              Reset
            </Button>

            <Button onClick={applyFilters}>
              Search
            </Button>
          </div>
        </div>

{selectedTag.length > 0 && (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "0.5rem",
      marginTop: "1rem",
    }}
  >
    {selectedTag.map((tag) => (
<Tag
  key={tag.id}
  type={getTagColor(tag.originalName)}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    }}
  >
    <span>Tag: {tag.text}</span>

    <button
      type="button"
      onClick={() => {
        setSelectedTag((prev) =>
          prev.filter((t) => t.id !== tag.id)
        );
      }}
      style={{
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "bold",
        padding: 0,
        lineHeight: 1,
      }}
    >
      ×
    </button>
  </div>
</Tag>
    ))}
  </div>
)}
      </div>

      {/* Data Table for Properties */}
      <DataTable rows={paginatedData} headers={tableHeaders}>
        {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => {
          return (
            <TableContainer title="Tags Report" >
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    expanded
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    value={searchTerm}
                  />
                </TableToolbarContent>
              </TableToolbar>
              
              {loading && (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <InlineLoading description="Loading tag report data..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              {success && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={success}
                    lowContrast
                    onClose={() => setSuccess("")}
                  />
                </div>
              )}

              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => {
  const { key, ...headerProps } = getHeaderProps({ header });

  return (
    <TableHeader key={key} {...headerProps}>
      {header.header}
    </TableHeader>
  );
})}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} style={{ textAlign: 'center', padding: '2rem' }}>
                        {!hasActiveFilters
                          ? 'Apply filter to view properties'
                          : error || `No properties found for the selected tag${selectedTag ? `: ${selectedTag.text}` : ''}`}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, rowIndex) => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        {headers.map((header) => (
                          <TableCell key={header.key}>
                            {header.key === 'verificationStatus' ? (
                              <Tag type={row[header.key] === 'Verified' ? 'green' : 'yellow'}>
                                {row[header.key]}
                              </Tag>
                            ) : header.key === 'status' ? (
                              <Tag type={row[header.key] === 'Active' ? 'green' : 'gray'}>
                                {row[header.key]}
                              </Tag>
                            ) : (
                              row[header.key]
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {filteredData.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredData.length}
            page={page}
            pageSize={pageSize}
            pageSizes={[100, 50, 20, 10, 5]}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}
    </>
  );
}